import { z } from 'zod';

import { HlcSchema, type Hlc } from './hlc.js';

/**
 * Suggestion-set **bundle** primitive (#251, part of #224 / 04-llm-role.md §14).
 *
 * A *bundle* groups a batch of facts that were produced together by a single
 * generation pass — bulk world-generation is the first consumer, but the
 * primitive is deliberately **producer-agnostic** (the `source` discriminator
 * also covers rollable-table draws and transcript-extraction #225). The
 * generation metadata is carried **once** in a `bundle.declare` meta-op rather
 * than smeared across every member fact; each member draft `fact.create` only
 * carries a back-pointer (`bundle_id` on `FactMetaShape`).
 *
 * Shape choices, mirroring the existing reserved-subject meta-op patterns:
 *   - `config.set` (`config-ops.ts`) rides ONE reserved subject (`__campaign`)
 *     because a campaign has exactly one config chain;
 *   - `pack.*` (`pack-ops.ts`) rides ONE reserved subject (`HOMEBREW_PACK_ID`)
 *     under the reserved `__pack` predicate, for the same reason.
 *
 * A campaign has MANY bundles, so — like entities, which each get their own
 * subject — a bundle is addressed by its own minted `bun_…` subject id and the
 * `bundle.declare` op chains under the reserved `__bundle` predicate slot. That
 * keeps bundle meta-ops out of every fact chain (the `__`-prefix is
 * engine-reserved and not user-addressable) while still giving each bundle a
 * stable, addressable chain (for regenerate-links + the fold registry).
 *
 * This module is the wire/shape + typed-schema layer only: the op payload, the
 * reserved predicate + id mint, a builder, and a type guard. The fold into a
 * bundle registry lives in `bundle-fold.ts`; the DO write gate lives in
 * `apps/do-campaign/src/append.ts`.
 */

// `crypto.randomUUID` is available at runtime in modern browsers, Web Workers,
// Cloudflare Workers, and Node 19+ — we don't want to pull DOM lib types into
// every consumer just to type this one call. Mirrors `identity.ts` / `entity.ts`.
declare const crypto: { randomUUID(): string };

/**
 * Predicate slot reserved for bundle meta-ops. Like `ENTITY_META_PREDICATE`
 * (`__entity`) / `PACK_META_PREDICATE` (`__pack`), the `__`-prefix keeps these
 * ops out of any fact chain and out of the user-declarable namespace. Every
 * `bundle.declare` op chains under `(bundle_id, __bundle)`.
 */
export const BUNDLE_META_PREDICATE = '__bundle';

/**
 * The `bun_…` prefix every bundle subject id carries. A bundle id names a
 * generation batch (a sibling of an entity id / campaign id — an op-addressing
 * id, NOT an open-vocabulary `mintVocabId` definition). Opaque + immutable.
 */
export const BUNDLE_ID_PREFIX = 'bun_';

/**
 * A bundle subject id: `bun_<uuidv4>`. Validated to the prefix only (the body
 * is an opaque uuid); `regenerated_from` is validated to the same shape so a
 * regen link can never point at a non-bundle subject.
 */
export const BundleIdSchema = z
  .string()
  .min(1)
  .refine((s) => s.startsWith(BUNDLE_ID_PREFIX), {
    message: `bundle_id must start with "${BUNDLE_ID_PREFIX}"`,
  });

/** Mint a new opaque bundle id (`bun_<uuidv4>`). */
export function mintBundleId(): string {
  return `${BUNDLE_ID_PREFIX}${crypto.randomUUID()}`;
}

/**
 * Where the bundle's facts came from — the producer discriminator that makes
 * this primitive reusable. `bulk_gen` is the first consumer (#224); `rollable`
 * (rollable-table draws) and `extraction` (transcript extraction, #225) reuse
 * the exact same bundle machinery without coupling it to bulk-gen.
 */
export const BundleSourceSchema = z.enum(['bulk_gen', 'rollable', 'extraction', 'combat_log']);
export type BundleSource = z.infer<typeof BundleSourceSchema>;

/**
 * Generation depth — how much fan-out the producer aimed for WITHIN the
 * config-backed hard cap (#218). Recorded for provenance / the regen UI; the
 * cap (not this field) is the enforced bound. Optional: a non-bulk-gen producer
 * (a rollable draw) may carry no depth.
 */
export const BundleDepthSchema = z.enum(['sparse', 'normal', 'dense']);
export type BundleDepth = z.infer<typeof BundleDepthSchema>;

/**
 * The resolved grounding/creativity posture the producer ran under (04 §14.6).
 * `auto` is the request-time sentinel; the worker resolves it to `bootstrap`
 * (little canon / seed target) or `in_world` (grounded in established
 * neighbors) and records the RESOLVED value here. Optional for producers that
 * have no mode concept.
 */
export const BundleModeSchema = z.enum(['auto', 'bootstrap', 'in_world']);
export type BundleMode = z.infer<typeof BundleModeSchema>;

/**
 * The `bundle.declare` op payload — the generation metadata carried once per
 * bundle. `.strict()` so no fact-only field (reveal/validity/belief/status)
 * can ride along: a bundle declaration is NOT a fact, it is a provenance record
 * in its own bucket (mirrors `config.set` being `.strict()`).
 *
 * `prompt` / `depth` / `mode` / `regenerated_from` are optional because they
 * are bulk-gen-shaped — a rollable or extraction bundle carries only the parts
 * it has. `bundle_id` MUST equal the op's subject (the DO gate enforces this);
 * carrying it in the payload too keeps the declaration self-describing for the
 * fold + export without a subject join.
 */
export const BundleDeclarePayloadSchema = z
  .object({
    kind: z.literal('bundle.declare'),
    bundle_id: BundleIdSchema,
    source: BundleSourceSchema,
    /** The free-text generation prompt (bulk-gen / extraction). Model input, not a UI string. */
    prompt: z.string().min(1).optional(),
    /** Requested generation depth, within the #218 cap. */
    depth: BundleDepthSchema.optional(),
    /** The resolved grounding posture (04 §14.6). */
    mode: BundleModeSchema.optional(),
    /**
     * Regenerate-with-direction link (04 §14.6): the bundle this one supersedes.
     * A new `BulkGenRequest` produces a NEW `bundle.declare` linked back here;
     * the old bundle's still-unaccepted drafts are dismissed via the
     * draft-reject path (part c). Validated to the same `bun_…` shape so the
     * link can never dangle onto a non-bundle subject.
     */
    regenerated_from: BundleIdSchema.optional(),
    /** The producer's HLC at declaration time (provenance ordering). */
    created_hlc: HlcSchema,
  })
  .strict();
export type BundleDeclarePayload = z.infer<typeof BundleDeclarePayloadSchema>;

// ─── Builder ───────────────────────────────────────────────────────────

/** Optional metadata accepted by the {@link bundleDeclare} builder. */
export interface BundleDeclareInput {
  prompt?: string;
  depth?: BundleDepth;
  mode?: BundleMode;
  regenerated_from?: string;
}

/**
 * Build a `bundle.declare` op payload. `bundleId` is both the payload field and
 * the op's subject (the DO gate enforces the equality). Optional fields are
 * omitted when absent so the payload stays minimal (and `.strict()`-clean).
 */
export function bundleDeclare(
  bundleId: string,
  source: BundleSource,
  createdHlc: Hlc,
  meta: BundleDeclareInput = {},
): BundleDeclarePayload {
  return {
    kind: 'bundle.declare',
    bundle_id: bundleId,
    source,
    created_hlc: createdHlc,
    ...(meta.prompt !== undefined ? { prompt: meta.prompt } : {}),
    ...(meta.depth !== undefined ? { depth: meta.depth } : {}),
    ...(meta.mode !== undefined ? { mode: meta.mode } : {}),
    ...(meta.regenerated_from !== undefined
      ? { regenerated_from: meta.regenerated_from }
      : {}),
  };
}

// ─── Type guard ──────────────────────────────────────────────────────────

/** Narrow an unknown op payload to a `bundle.declare` payload (shape-only check). */
export function isBundleDeclare(payload: unknown): payload is BundleDeclarePayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'bundle.declare'
  );
}
